import { useEffect, useRef, useCallback } from 'react';

import { postMyTrainingProgress } from '../../../services/trainingService';

const HEARTBEAT_INTERVAL_MS = 30_000;

/**
 * 30s heartbeat that posts progress for the currently open content item.
 *
 * The consumer passes a `getState()` callback that returns the latest
 * { contentItemId, lastPositionSeconds, watchedSeconds, viewedPages,
 *   clientCompleted } — we read it right before each fire so we always send
 * fresh numbers even though the timer is set up once.
 *
 * Returns a `flush()` function the consumer can call on unmount or after an
 * end-of-media event to push progress immediately (without waiting for the
 * next 30s tick).
 */
export default function useProgressHeartbeat(assignmentId, getState, onResponse) {
  const getStateRef = useRef(getState);
  const onResponseRef = useRef(onResponse);
  const stoppedRef = useRef(false);

  useEffect(() => { getStateRef.current = getState; }, [getState]);
  useEffect(() => { onResponseRef.current = onResponse; }, [onResponse]);

  const fire = useCallback(async (extra) => {
    if (stoppedRef.current) return;
    if (!assignmentId) return;
    const state = getStateRef.current?.();
    if (!state?.contentItemId) return;
    try {
      const res = await postMyTrainingProgress(assignmentId, {
        contentItemId: state.contentItemId,
        lastPositionSeconds: state.lastPositionSeconds,
        watchedSeconds: state.watchedSeconds,
        viewedPages: state.viewedPages,
        clientCompleted: extra?.clientCompleted ?? state.clientCompleted ?? false,
      });
      onResponseRef.current?.(res?.data?.data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400 || status === 403) {
        stoppedRef.current = true;
      }
    }
  }, [assignmentId]);

  useEffect(() => {
    const id = setInterval(fire, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [fire]);

  return fire;
}
